import { useSceneEvents } from "@/components/common/scene-events-context";
import { CadRenderer } from "@/components/r3f/renderers/cad-renderer";
import { CadModelObject } from "@/object-cache";
import type { ThreeEvent } from "@react-three/fiber";
import { JSX, useCallback, useEffect } from "react";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";

type FlyWalkCadRendererProps = {
  /** CAD model object in the current scene */
  cadModel: CadModelObject | null;
  /** Whether picking is enabled on the CAD model or not */
  pickingEnabled: boolean;
};

/** @returns a component to render a CAD model in flywalk mode */
export function FlyWalkCadRenderer({
  cadModel,
  pickingEnabled,
}: FlyWalkCadRendererProps): JSX.Element | null {
  const { clippingPlanes, onModelHovered, onModelClicked, onModelZoomed } =
    useToolsAcquaintance();

  // When the clipping planes are changed via the "auto clip box" or
  // "reset clip box" functions, the CAD subscene must be invalidated
  const { invalidateCadScene } = useSceneEvents();
  useEffect(() => {
    invalidateCadScene.emit();
  }, [clippingPlanes, invalidateCadScene]);

  const onPointerMove = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      if (cadModel) {
        onModelHovered(ev, cadModel.iElement.id);
      }
    },
    [cadModel, onModelHovered],
  );

  const onClick = useCallback(
    (ev: ThreeEvent<MouseEvent>) => {
      if (cadModel) {
        onModelClicked(ev, cadModel.iElement.id);
      }
    },
    [cadModel, onModelClicked],
  );

  const onWheel = useCallback(
    (ev: ThreeEvent<WheelEvent>) => {
      if (cadModel) {
        onModelZoomed(ev, cadModel.iElement.id);
      }
    },
    [cadModel, onModelZoomed],
  );

  if (!cadModel) {
    return null;
  }

  return (
    <CadRenderer
      cadModel={cadModel}
      onPointerMove={pickingEnabled ? onPointerMove : undefined}
      onClick={pickingEnabled ? onClick : undefined}
      onWheel={pickingEnabled ? onWheel : undefined}
      clippingPlanes={clippingPlanes}
    />
  );
}
